import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";

const STRATEGIES = [
  { key: "protect_margin", label: "Protect Margin", hint: "Prioritize margin over being the cheapest. No auto-beat." },
  { key: "match_market",   label: "Match Market",   hint: "Aim near the observed market median." },
  { key: "aggressive",     label: "Aggressive",     hint: "Undercut the observed low, but never drop below your minimum margin." },
];

export default function SettingsPricingStrategy() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/market/strategy").then((r) => setPrefs(r.data)).catch(() => {});
  }, []);

  const on = (k) => (v) => setPrefs({ ...prefs, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/market/strategy", prefs);
      toast.success("Pricing strategy saved.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (!prefs) return <DashboardLayout><div className="text-sm text-[#3B424B]">Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <h1 className="text-2xl sm:text-3xl font-bold" data-testid="pricing-strategy-heading">Pricing Strategy</h1>
      <p className="mt-1 text-sm text-[#3B424B] max-w-2xl">
        Configure how TreadMargin makes pricing recommendations. Your minimum margin is a hard floor — no
        recommendation ever drops below it. TreadMargin never changes prices automatically.
      </p>

      <div className="mt-6 tm-card bg-white p-6 max-w-2xl space-y-6">
        <div>
          <label className="tm-label">Minimum acceptable gross margin (%)</label>
          <input
            type="number"
            min={0}
            max={90}
            step="0.5"
            className="tm-input max-w-[160px]"
            value={prefs.min_margin_pct}
            onChange={(e) => on("min_margin_pct")(parseFloat(e.target.value) || 0)}
            data-testid="pricing-min-margin"
          />
          <p className="mt-1 text-xs text-[#3B424B]">Recommendations below this margin will be blocked.</p>
        </div>

        <div>
          <div className="tm-label">Competitive strategy</div>
          <div className="mt-2 grid gap-2">
            {STRATEGIES.map((s) => (
              <label key={s.key} className={`tm-card p-3 cursor-pointer border ${prefs.strategy === s.key ? "border-[var(--tm-charcoal)] bg-[var(--tm-off)]" : "border-[var(--tm-border)]"}`}>
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    checked={prefs.strategy === s.key}
                    onChange={() => on("strategy")(s.key)}
                    className="mt-1 accent-[#14624A]"
                    data-testid={`pricing-strategy-${s.key}`}
                  />
                  <div>
                    <div className="font-semibold text-sm">{s.label}</div>
                    <div className="text-xs text-[#3B424B] mt-0.5">{s.hint}</div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="tm-label">Market location context (informational)</label>
          <input
            className="tm-input"
            placeholder="e.g. Barrie, ON"
            value={prefs.location_context || ""}
            onChange={(e) => on("location_context")(e.target.value)}
            data-testid="pricing-location"
          />
          <p className="mt-1 text-xs text-[#3B424B]">Used to caption the Market Overview page. Radius search is not enabled yet.</p>
        </div>

        <div className="pt-4 border-t border-[var(--tm-border)]">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!prefs.share_benchmark}
              onChange={(e) => on("share_benchmark")(e.target.checked)}
              className="mt-1 h-4 w-4 accent-[#14624A]"
              data-testid="pricing-share-benchmark"
            />
            <div className="text-sm">
              <div className="font-semibold">Share to unlock — Regional dealer benchmark</div>
              <p className="mt-1 text-xs text-[#3B424B] leading-relaxed">
                Contribute your <b>selling prices</b> (anonymously) to the TreadMargin dealer network in your province, and see the pooled median/low/high across other opted-in dealers as a live provider on the Market page. Individual dealers are <b>never named</b>. A group only becomes visible once <b>3+ dealers</b> in your province have contributed to it (k-anonymity).
              </p>
            </div>
          </label>
        </div>

        <button onClick={save} disabled={busy} className="tm-btn-primary" data-testid="pricing-strategy-save">
          {busy ? "Saving…" : "Save Strategy"}
        </button>
      </div>
    </DashboardLayout>
  );
}
